import React, { useRef, useEffect, useState, MouseEvent } from 'react';
import { FaBold, FaItalic, FaUnderline, FaStrikethrough, FaListOl, FaListUl, FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify, FaLink, FaUnlink, FaImage, FaEraser, FaEdit, FaVideo } from 'react-icons/fa';
import { Button, ButtonGroup, Box, Menu, MenuItem } from '@mui/material';
import editorLabels from '../assets/json/editorLabels.json';
import { uploadFile } from '../services/infoService';
import Swal from 'sweetalert2';
import '../assets/css/EditorComponent.css';

interface WysiwygEditorProps {
    value: string;
    onChange: (value: string, videoUrl?: string) => void;
    options: string[];
    isSoporte?: boolean;
}

const WysiwygEditor: React.FC<WysiwygEditorProps> = ({ value = '', options = [], onChange, isSoporte = false }) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [optionsActive, setOptionsActive] = useState<string[]>([]);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedVideo, setSelectedVideo] = useState<HTMLVideoElement | null>(null);

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value;
        }
    }, [value]);

    useEffect(() => {
        const handleSelectionChange = () => {
            const newOptionsActive = options.filter(option => document.queryCommandState(option));
            setOptionsActive(newOptionsActive);
        };

        document.addEventListener('selectionchange', handleSelectionChange);
        return () => {
            document.removeEventListener('selectionchange', handleSelectionChange);
        };
    }, [options]);

    const handleInput = () => {
        if (editorRef.current) {
            const newValue = editorRef.current.innerHTML;
            if (newValue !== value) {
                onChange(newValue);
            }
        }
    };

    const execCommand = (command: string, value: string = '') => {
        document.execCommand(command, false, value);
    };

    const handleVideoClick = async (video: HTMLVideoElement) => {
        const { value: url, isConfirmed } = await Swal.fire({
            title: 'Cambiar URL del video',
            input: 'url',
            inputPlaceholder: 'http://',
            inputValue: video.src,
            showCancelButton: true,
            confirmButtonText: 'Guardar',
            cancelButtonText: 'Cancelar',
            allowEscapeKey: false,
            inputValidator: (value) => {
                if (!value) {
                    return 'Debes ingresar una URL';
                }
            },
            inputAttributes: {
                'aria-label': 'URL'
            }
        });

        if (!isConfirmed) return;

        const isVideoUrl = (url: string) => {
            const videoExtensions = ['.mp4', '.webm', '.ogg'];
            return videoExtensions.some(ext => url.endsWith(ext));
        };

        if (url && isVideoUrl(url)) {
            video.src = url;
        } else {
            Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'La URL proporcionada no es un enlace de video válido.',
            });
        }
    };

    const handleEditHtml = async () => {
        const { value: html, isConfirmed } = await Swal.fire({
            title: 'Editar HTML',
            input: 'textarea',
            inputValue: editorRef.current?.innerHTML,
            showCancelButton: true,
            confirmButtonText: 'Guardar',
            cancelButtonText: 'Cancelar',
            allowEscapeKey: false,
            inputValidator: (value) => {
                if (!value) {
                    return 'El contenido no puede estar vacío';
                }
            },
            inputAttributes: {
                'aria-label': 'HTML',
            },
            customClass: {
                container: 'textarea-editor-container',
                input: 'textarea-editor',

            },
        });

        if (isConfirmed && editorRef.current) {
            editorRef.current.innerHTML = html;
            onChange(html);
        }
    };

    const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        const target = event.target as HTMLElement;
        if (target.tagName === 'VIDEO') {
            setSelectedVideo(target as HTMLVideoElement);
            setMenuAnchorEl(target);
        }
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedVideo(null);
    };

    const handleMenuOptionClick = async (option: string) => {
        if (option === 'changeUrl' && selectedVideo) {
            await handleVideoClick(selectedVideo);
        } else if (option === 'remove' && selectedVideo) {
            selectedVideo.remove();
        }
        if (!selectedVideo) return;
        console.log('Contenido después de la acción del menú:', selectedVideo.src);
        onChange(editorRef.current?.innerHTML || '', selectedVideo.src.includes('ayuda.colpegasus.com') ? selectedVideo.src : '');
        handleMenuClose();
    };

    const insertMediaHtml = (command: string, url: string) => {
        if (!editorRef.current) return;

        if (command === 'insertVideo') {
            editorRef.current.innerHTML += `<video controls autoplay src="${url}" width="100%" height="auto">Lo sentimos, su navegador no admite videos incrustados.</video>`;
        } else {
            editorRef.current.innerHTML += `<img src="${url}" alt="Imagen" width="100%" height="auto" />`;
        }
        onChange(editorRef.current.innerHTML);
    };

    const promptMediaUrl = async (command: string): Promise<string | null> => {
        const label = command === 'insertVideo' ? 'URL del video' : 'URL de la imagen';
        const { value: url, isConfirmed } = await Swal.fire({
            title: `Ingresar ${label}`,
            input: 'url',
            inputPlaceholder: 'https://',
            showCancelButton: true,
            confirmButtonText: 'Insertar',
            cancelButtonText: 'Cancelar',
            allowEscapeKey: false,
            inputValidator: (value) => {
                if (!value) return 'Debes ingresar una URL';
            },
            inputAttributes: { 'aria-label': 'URL' }
        });

        return isConfirmed && url ? url : null;
    };

    const handleFileUpload = async (file: File, command: string) => {
        const tipo = command === 'insertVideo' ? 'video' : 'imagen';

        console.log(`Subiendo ${tipo}:`, file);

        // Guardamos la instancia de SweetAlert para poder manipularla
        Swal.fire({
            title: 'Subiendo archivo...',
            html: 'Progreso: <b>0%</b>', // Usamos HTML para poder cambiarlo dinámicamente
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });

        try {
            // Pasamos la función que actualiza el SweetAlert en cada fragmento
            const url = await uploadFile(file, tipo, (porcentaje) => {
                const htmlContainer = Swal.getHtmlContainer();
                if (htmlContainer) {
                    const b = htmlContainer.querySelector('b');
                    if (b) {
                        b.innerText = `${porcentaje}%`;
                    }
                }
            });

            insertMediaHtml(command, url);

            Swal.fire({
                icon: 'success',
                title: 'Archivo subido',
                text: 'El archivo se insertó correctamente en el contenido.',
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error al subir',
                text: error instanceof Error ? error.message : 'No se pudo subir el archivo.',
            });
        }
    };


    const handleMediaInsert = async (command: string) => {
        if (command === 'insertVideo') {
            const existingVideo = editorRef.current?.querySelector('video');
            if (existingVideo) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Solo un video permitido',
                    text: 'Ya existe un video en el contenido. Por favor, elimínalo antes de insertar otro.',
                });
                return;
            }
        }
        if (isSoporte) {
            // const mediaLabel = command === 'insertVideo' ? 'video' : 'imagen';
            // const result = await Swal.fire({
            //     title: `Insertar ${mediaLabel}`,
            //     text: '¿Desea subir un archivo desde su equipo o usar una URL externa?',
            //     icon: 'question',
            //     showCancelButton: true,
            //     // showDenyButton: true,
            //     confirmButtonText: 'Subir archivo',
            //     cancelButtonText: 'Cancelar',
            // });

            // if (result.isConfirmed) {
            if (command === 'insertVideo') {
                videoInputRef.current?.click();
            } else {
                imageInputRef.current?.click();
            }
            // return;
            // }

            // if (result.isDenied) {
            //     const url = await promptMediaUrl(command);
            //     if (url) insertMediaHtml(command, url);
            // }
            return;
        }

        const url = await promptMediaUrl(command);
        if (!url) return;

        if (command === 'insertImage') {
            const img = new Image();
            img.src = url;
            img.onload = () => insertMediaHtml(command, url);
            img.onerror = () => {
                Swal.fire({
                    icon: 'error',
                    title: 'Oops...',
                    text: 'La URL de la imagen no es válida.',
                });
            };
        } else {
            insertMediaHtml(command, url);
        }
    };

    const onVideoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const typeFiles = ['video/mp4', 'video/webm', 'video/ogg'];
        if (file && !typeFiles.includes(file.type)) {
            Swal.fire({
                icon: 'error',
                title: 'Tipo de archivo no permitido',
                text: 'Por favor, selecciona un archivo de video válido (mp4, webm, ogg).',
            });
            event.target.value = '';
            return;
        }
        if (file) handleFileUpload(file, 'insertVideo');
        event.target.value = '';
    };

    const onImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) handleFileUpload(file, 'insertImage');
        event.target.value = '';
    };

    return (
        <Box sx={{ border: '1px solid #ccc', borderRadius: 2, padding: 2 }} className="editor-component">
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%", marginBottom: 1 }}>
                <ButtonGroup aria-label="" className='lg:!flex !grid grid-cols-8 sm:!grid-cols-10 md:!grid-cols- lg:!grid-cols-16 xl:!grid-cols-13'>
                    {options.map((command) => (
                        <Button
                            key={command}
                            onMouseDown={async (e) => {
                                e.preventDefault();

                                setOptionsActive([command]);

                                if (['insertVideo', 'insertImage'].includes(command)) {
                                    await handleMediaInsert(command);
                                    setOptionsActive([]);
                                    return;
                                } else if (command === 'createLink') {
                                    const selection = window.getSelection();
                                    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
                                        Swal.fire({
                                            icon: 'warning',
                                            title: 'Selecciona texto',
                                            text: 'Por favor, selecciona el texto que deseas enlazar.',
                                        });
                                        setOptionsActive([]);
                                        return;
                                    }

                                    const { value: url, isConfirmed } = await Swal.fire({
                                        title: 'Insertar enlace',
                                        input: 'url',
                                        inputPlaceholder: 'http://',
                                        showCancelButton: true,
                                        confirmButtonText: 'Insertar',
                                        cancelButtonText: 'Cancelar',
                                        allowEscapeKey: false,
                                        inputValidator: (value) => {
                                            if (!value) {
                                                return 'Debes ingresar una URL';
                                            }
                                        },
                                        inputAttributes: {
                                            'aria-label': 'URL'
                                        }
                                    });

                                    if (!isConfirmed) {
                                        setOptionsActive([]);
                                        return;
                                    }

                                    execCommand(command, url);
                                }
                                execCommand(command);
                            }}
                            sx={{ backgroundColor: optionsActive.includes(command) ? 'rgba(6, 82, 221, 1);' : '', color: optionsActive.includes(command) ? 'white' : 'rgba(6, 82, 221, 1)' }}
                            title={editorLabels[command as keyof typeof editorLabels] || ''}
                        >
                            {command === 'bold' && <FaBold title="Negrita" />}
                            {command === 'italic' && <FaItalic title="Cursiva" />}
                            {command === 'underline' && <FaUnderline title="Subrayado" />}
                            {command === 'strikeThrough' && <FaStrikethrough title="Tachado" />}
                            {command === 'insertOrderedList' && <FaListOl title="Lista ordenada" />}
                            {command === 'insertUnorderedList' && <FaListUl title="Lista desordenada" />}
                            {command === 'justifyLeft' && <FaAlignLeft title="Alinear a la izquierda" />}
                            {command === 'justifyCenter' && <FaAlignCenter title="Alinear al centro" />}
                            {command === 'justifyRight' && <FaAlignRight title="Alinear a la derecha" />}
                            {command === 'justifyFull' && <FaAlignJustify title="Alinear justificado" />}
                            {command === 'insertVideo' && <FaVideo title="Insertar video" />}
                            {command === 'createLink' && <FaLink title="Crear enlace" />}
                            {command === 'unlink' && <FaUnlink title="Eliminar enlace" />}
                            {command === 'insertImage' && <FaImage title="Insertar imagen" />}
                            {command === 'removeFormat' && <FaEraser title="Eliminar formato" />}
                        </Button>
                    ))}
                    <Button
                        onMouseDown={(e) => {
                            e.preventDefault();
                            handleEditHtml();
                        }}
                        title='Editar HTML'
                    >
                        <FaEdit />
                    </Button>
                </ButtonGroup>
            </Box>
            <Box
                ref={editorRef}
                contentEditable
                className="editor"
                onInput={handleInput}
                sx={{ minHeight: '200px', padding: 4 }}
                onContextMenu={handleContextMenu}
            />
            <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/webm,video/ogg"
                style={{ display: 'none' }}
                onChange={onVideoFileChange}
            />
            <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={onImageFileChange}
            />
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <MenuItem onClick={() => {
                    handleMenuClose()
                    handleMenuOptionClick('changeUrl')
                }}>
                    Cambiar URL del video
                </MenuItem>
                <MenuItem onClick={() => {
                    handleMenuClose()
                    handleMenuOptionClick('remove')
                }}>
                    Eliminar video
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default WysiwygEditor;